import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useFieldArray, useForm, FormProvider } from 'react-hook-form';
import { BsPlus, BsTrash } from 'react-icons/bs';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  ApiError,
  BoardCertification,
  BoardCertificationTypeEnum,
  Certification,
  CertificationTypeEnum,
  License,
  LicenseTypeEnum,
  ProviderCredentialingLicenses,
  ProviderService,
} from '../../../api';
import { Card } from '../../../app/components/Card';
import { FileUploader } from '../../../app/components/FileUploader';
import { FormCheckboxField } from '../../../app/components/FormCheckboxField';
import { FormFeedback } from '../../../app/components/FormFeedback';
import { FormFieldVisibility, FormLabel } from '../../../app/components/FormLabel';
import { FormTextField } from '../../../app/components/FormTextField';
import { LicensesUploader } from '../../../app/components/LicensesUploader';
import { SelectController } from '../../../app/components/SelectController';
import { ServerErrorsAlert } from '../../../app/components/ServerErrorsAlert';
import { UnsavedChangesFormPrompt } from '../../../app/components/UnsavedChangesFormPrompt';
import { FormSubmitContext } from '../../../app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';
import { userQueryKeys } from '../../../app/query-keys-factories/user';
import { handleApiError } from '../../../app/utils/errorhandling';
import {
  generateShowFieldErrors,
  generateZEnumFromEnum,
  ShowFieldErrors,
} from '../../../utils/forms';
import { ProviderProfileCreateOutletContext } from '../../Registration/ProviderProfileCreate';
import { ProviderFormContainer } from './ProviderFormContainer';
import {
  addFileField,
  getLicenseSchema,
  getProviderLicenses,
  getProviderLicensesFromForm,
  sortCertificationsByType,
} from './utils';

const validationSchema = z.object({
  licenses: z.object({
    [LicenseTypeEnum.MEDICAL]: z
      .array(getLicenseSchema().element)
      .min(1, 'At least one Medical License is required'),
    [LicenseTypeEnum.DEA]: getLicenseSchema().optional(),
    [LicenseTypeEnum.CSR]: getLicenseSchema().optional(),
  }),
  board_certifications: z
    .array(
      z
        .object({
          id: z.string().uuid().optional(),
          type: generateZEnumFromEnum(BoardCertificationTypeEnum),
          file__id: z.string().uuid().optional(),
          file__file: z.union([z.string().url(), z.string().length(0), z.null()]).optional(),
          expiration_date: z.union([
            z.string().length(0),
            z.null(),
            z.coerce.date().transform((val: Date) => {
              return new Date(val.valueOf() + val.getTimezoneOffset() * 60 * 1000);
            }),
          ]),
          board_eligible: z.boolean(),
        })
        .superRefine((data, ctx) => {
          // Validate expiration date if there's a file
          if (data.file__file && !data.expiration_date) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Required',
              path: ['expiration_date'],
            });
          }
        })
    )
    .min(1, 'At least one Board Certification is required'),
  certifications: z.array(
    z
      .object({
        id: z.string().uuid().optional(),
        type: generateZEnumFromEnum(CertificationTypeEnum),
        file__id: z.string().uuid().optional(),
        file__file: z.union([z.string().url(), z.string().length(0), z.null()]).optional(),
        expiration_date: z.union([
          z.null(),
          z.string().length(0),
          z.coerce.date().transform((val: Date) => {
            return new Date(val.valueOf() + val.getTimezoneOffset() * 60 * 1000);
          }),
        ]),
      })
      .superRefine((data, ctx) => {
        // Validate expiration date if there's a file
        if (data.file__file && !data.expiration_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['expiration_date'],
          });
        }
      })
  ),
  has_imlc: z.boolean().optional(),
  imlc_expiration_date: z.union([z.string(), z.null(), z.date()]).optional(),
});

export const ProviderLicensesForm = () => {
  useDocumentTitle('Licenses and Certificates');
  const { formRef, onSuccess } = useOutletContext<ProviderProfileCreateOutletContext>() ?? {};
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<ShowFieldErrors>({});

  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      licenses: {
        [LicenseTypeEnum.MEDICAL]: [],
        [LicenseTypeEnum.DEA]: [],
        [LicenseTypeEnum.CSR]: [],
      },
      board_certifications: [],
      certifications: [],
      has_imlc: false,
      imlc_expiration_date: null,
    },
  });

  const {
    fields: boardCertificationFields,
    append: addBoardCertification,
    remove: removeBoardCertification,
    replace: replaceBoardCertifications,
  } = useFieldArray({
    control: methods.control,
    name: 'board_certifications',
    shouldUnregister: true,
  });

  const {
    fields: certificationFields,
    append: addCertification,
    remove: removeCertification,
    replace: replaceCertifications,
  } = useFieldArray({
    control: methods.control,
    name: 'certifications',
    shouldUnregister: true,
  });

  const { data: provider, isLoading } = useQuery({
    queryKey: providerQueryKeys.detailCredentialingLicenses(),
    queryFn: () => ProviderService.providerCurrentCredentialingLicensesRetrieve(),
  });

  useEffect(() => {
    if (!provider || isInitialized) return;

    const formData = {
      ...provider,
      licenses: getProviderLicenses(provider?.licenses),
      board_certifications: sortCertificationsByType(
        provider?.board_certifications?.map(addFileField) ?? []
      ),
      certifications: sortCertificationsByType(provider?.certifications?.map(addFileField) ?? []),
    };

    // Update form values
    methods.reset(formData, {
      keepDefaultValues: false,
      keepDirty: false,
      keepValues: false,
    });

    // Explicitly update field arrays
    replaceBoardCertifications(formData.board_certifications);
    replaceCertifications(formData.certifications);

    setIsInitialized(true);
  }, [provider, isInitialized]);

  // Force re-render of field arrays when data is loaded
  useEffect(() => {
    if (isInitialized) {
      methods.trigger();
    }
  }, [isInitialized]);

  const queryClient = useQueryClient();
  const mutationProviderCurrentCredentialingLicensesUpdate = useMutation({
    mutationFn: async (data: ProviderCredentialingLicenses) => {
      await ProviderService.providerCurrentCredentialingLicensesUpdate({ requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(providerQueryKeys.all());
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.detail('current'),
      });
    },
  });

  function handleClearErrorsAlert() {
    setShowGlobalServerErrors(false);
  }

  const handleSubmitClick = () => {
    methods.clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  };

  const onSubmit = async (data) => {
    try {
      const licenses: License[] = [
        ...(data.licenses[LicenseTypeEnum.MEDICAL] ?? []),
        ...(data.licenses[LicenseTypeEnum.DEA] ?? []),
        ...(data.licenses[LicenseTypeEnum.CSR] ?? []),
      ].map(getProviderLicensesFromForm<License>);

      await mutationProviderCurrentCredentialingLicensesUpdate.mutateAsync({
        id: data.id,
        licenses,
        board_certifications: data.board_certifications.map(
          getProviderLicensesFromForm<BoardCertification>
        ),
        certifications: data.certifications.map(getProviderLicensesFromForm<Certification>),
        has_imlc: data.has_imlc,
        imlc_expiration_date: data.imlc_expiration_date,
      });
      toast.success('Provider Licenses Data Updated Successfully');
      onSuccess?.();
    } catch (errors) {
      if (errors.status === 400) {
        const fieldErrors = omit(errors.body, ['non_field_errors']);
        setServerErrors(errors);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
      handleApiError(errors);
    }
  };

  const emptyBoardCertification = {
    type: '',
    file__file: null,
    expiration_date: null,
    board_eligible: false,
    file__id: undefined,
  };

  const emptyCertification = {
    type: '',
    file__file: null,
    expiration_date: null,
    file__id: undefined,
  };

  if (isLoading || !isInitialized) {
    return <div>Loading...</div>;
  }

  return (
    <Card className="p-4">
      <h2 className="border-bottom pb-2 mb-4">Licenses & Certificates</h2>
      <FormProvider {...methods}>
        <Form noValidate onSubmit={methods.handleSubmit(onSubmit)} ref={formRef}>
          <Row className="d-flex align-items-center flex-row flex-wrap">
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="has_imlc"
                  label="Do you have a Compact License (IMLC)?"
                  {...methods.register('has_imlc')}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              {(methods.watch('has_imlc') || methods.watch('imlc_expiration_date')) && (
                <Form.Group className="mb-3">
                  <FormLabel visibility={FormFieldVisibility.CONNECTED}>
                    IMLC Expiration Date
                  </FormLabel>
                  <Form.Control
                    type="date"
                    {...methods.register('imlc_expiration_date')}
                    isInvalid={!!methods.formState.errors.imlc_expiration_date}
                    onChange={(e) => {
                      const value = e.target.value;
                      methods.setValue('imlc_expiration_date', value || null, {
                        shouldValidate: true,
                      });
                    }}
                  />
                  <FormFeedback
                    clientError={methods.formState.errors.imlc_expiration_date}
                    serverErrors={
                      showServerFieldErrors.imlc_expiration_date
                        ? serverErrors?.body?.imlc_expiration_date
                        : null
                    }
                  />
                </Form.Group>
              )}
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <FormLabel required={true} visibility={FormFieldVisibility.CONNECTED}>
              Medical License(s)
            </FormLabel>
            <LicensesUploader
              controlId="licenses"
              licenseType={LicenseTypeEnum.MEDICAL}
              control={methods.control}
              register={methods.register}
              errors={methods.formState.errors}
              className="provider-file-input"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              itemCaption="Medical License"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <FormLabel visibility={FormFieldVisibility.CONNECTED}>DEA License(s)</FormLabel>
            <LicensesUploader
              controlId={`licenses`}
              licenseType={LicenseTypeEnum.DEA}
              control={methods.control}
              register={methods.register}
              errors={methods.formState.errors}
              className="provider-file-input"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              itemCaption="DEA License"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <FormLabel visibility={FormFieldVisibility.CONNECTED} required={true}>
              Board Certifications
            </FormLabel>
            <FormFeedback
              clientError={
                methods.formState.errors?.board_certifications?.root ||
                methods.formState.errors?.board_certifications
              }
            />
            <ul className="list-unstyled">
              {boardCertificationFields.map((item, index) => (
                <li key={item.id} className="mb-3 border bg-light rounded p-3">
                  <Row className="align-items-start">
                    <Col xs={12}>
                      <Row className="justify-content-end">
                        <Col>
                          <span className="text-primary fw-bold">
                            Board Certificate{item.type ? `: ${item.type}` : ' (New)'}
                          </span>
                        </Col>
                        <Col xs="auto" className="ps-0">
                          <Button
                            className="text-danger p-0"
                            type="button"
                            variant="link"
                            size="lg"
                            onClick={() => removeBoardCertification(index)}
                          >
                            <BsTrash />
                          </Button>
                        </Col>
                      </Row>
                      <Row className="d-flex flex-row flex-wrap align-items-end mb-2">
                        <Col xs={12} sm={7}>
                          <FormLabel
                            className="text-field-label"
                            visibility={FormFieldVisibility.CONNECTED}
                          >
                            Type<span className="text-danger"> *</span>
                          </FormLabel>
                          <SelectController
                            name={`board_certifications.${index}.type`}
                            control={methods.control}
                            options={Object.entries(window.BOARD_CERTIFICATION_TYPES).map(
                              ([value, label]) => ({
                                value,
                                label,
                              })
                            )}
                            isClearable={false}
                            isMulti={false}
                            placeholder="Choose One"
                            isInvalid={
                              !!methods.formState.errors.board_certifications?.[index]?.type ||
                              !!showServerFieldErrors.board_certifications?.[index.toString()]?.type
                            }
                            onChange={() => {
                              setShowServerFieldErrors((prev) => {
                                const newErrors = { ...prev };
                                if (newErrors.board_certifications?.[index.toString()]?.type) {
                                  delete newErrors.board_certifications[index.toString()].type;
                                }
                                return newErrors;
                              });
                            }}
                          />
                          <FormFeedback
                            clientError={
                              methods.formState.errors.board_certifications?.[index]?.type
                            }
                            serverErrors={serverErrors?.body.board_certifications?.[index]?.type}
                          />
                        </Col>
                        <Col xs={12} sm={3}>
                          <FormTextField
                            type="date"
                            className=""
                            name={`board_certifications.${index}.expiration_date`}
                            label="Expiration Date"
                            serverErrors={serverErrors}
                            showServerFieldErrors={showServerFieldErrors}
                            setShowServerFieldErrors={setShowServerFieldErrors}
                            errors={methods.formState.errors}
                            register={methods.register}
                          />
                        </Col>
                        <Col xs={12} sm={2}>
                          <FormCheckboxField
                            name={`board_certifications.${index}.board_eligible`}
                            label="Board Eligible"
                            className="ms-1 mt-2"
                            serverErrors={serverErrors}
                            showServerFieldErrors={showServerFieldErrors}
                            setShowServerFieldErrors={setShowServerFieldErrors}
                            errors={methods.formState.errors}
                            register={methods.register}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <Form.Group controlId={`board_certifications.${index}.file__file`}>
                            <Form.Label className="text-field-label">File</Form.Label>
                            <FileUploader
                              name={`board_certifications.${index}.file__file`}
                              control={methods.control}
                              removeButtonText="Replace File"
                              className="provider-file-input"
                              dest="providers_documents"
                              isClearable={true}
                              disabledPreview={true}
                              isInvalid={
                                !!methods.formState.errors.board_certifications?.[index]?.file ||
                                !!showServerFieldErrors.board_certifications?.[index.toString()]
                                  ?.file__file
                              }
                            />
                            <FormFeedback
                              clientError={
                                methods.formState.errors.board_certifications?.[index]?.file__file
                              }
                              serverErrors={
                                serverErrors?.body.board_certifications?.[index]?.file__file
                              }
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </li>
              ))}
            </ul>
            <Button
              className="d-flex align-items-center"
              variant="outline-primary"
              type="button"
              onClick={() => addBoardCertification(emptyBoardCertification)}
            >
              <BsPlus className="me-1" /> Add Board Certification
            </Button>
          </Form.Group>
          <Form.Group className="mb-3">
            <FormLabel visibility={FormFieldVisibility.CONNECTED}>
              Life Support Certifications
            </FormLabel>
            <ul className="list-unstyled">
              {certificationFields.map((item, index) => (
                <li key={item.id} className="mb-3 border bg-light rounded p-3">
                  <Row className="align-items-start">
                    <Col xs={12}>
                      <Row className="justify-content-end">
                        <Col>
                          <span className="text-primary fw-bold">
                            Life Support Certificate{item.type ? `: ${item.type}` : ' (New)'}
                          </span>
                        </Col>
                        <Col xs="auto" className="ps-0">
                          <Button
                            className="text-danger p-0"
                            type="button"
                            variant="link"
                            size="lg"
                            onClick={() => removeCertification(index)}
                          >
                            <BsTrash />
                          </Button>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} sm={6}>
                          <FormLabel
                            className="text-field-label"
                            visibility={FormFieldVisibility.CONNECTED}
                          >
                            Type<span className="text-danger"> *</span>
                          </FormLabel>
                          <SelectController
                            name={`certifications.${index}.type`}
                            control={methods.control}
                            options={Object.entries(window.CERTIFICATION_TYPES).map(
                              ([value, label]) => ({
                                value,
                                label,
                              })
                            )}
                            isClearable={false}
                            isMulti={false}
                            placeholder="Choose One"
                            isInvalid={
                              !!methods.formState.errors.certifications?.[index]?.type ||
                              !!showServerFieldErrors.certifications?.[index.toString()]?.type
                            }
                            onChange={() => {
                              setShowServerFieldErrors((prev) => {
                                const newErrors = { ...prev };
                                if (newErrors.certifications?.[index.toString()]?.type) {
                                  delete newErrors.certifications[index.toString()].type;
                                }
                                return newErrors;
                              });
                            }}
                          />
                          <FormFeedback
                            clientError={methods.formState.errors.certifications?.[index]?.type}
                            serverErrors={serverErrors?.body.certifications?.[index]?.type}
                          />
                        </Col>
                        <Col xs={12} sm={6}>
                          <FormTextField
                            type="date"
                            name={`certifications.${index}.expiration_date`}
                            label="Expiration Date"
                            serverErrors={serverErrors}
                            showServerFieldErrors={showServerFieldErrors}
                            setShowServerFieldErrors={setShowServerFieldErrors}
                            errors={methods.formState.errors}
                            register={methods.register}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <Form.Group controlId={`certifications.${index}.file__file`}>
                            <Form.Label className="text-field-label">File</Form.Label>
                            <FileUploader
                              name={`certifications.${index}.file__file`}
                              control={methods.control}
                              className="provider-file-input"
                              removeButtonText="Replace File"
                              dest="providers_documents"
                              isClearable={true}
                              disabledPreview={true}
                              isInvalid={
                                !!methods.formState.errors.certifications?.[index]?.file ||
                                !!showServerFieldErrors.certifications?.[index.toString()]
                                  ?.file__file
                              }
                            />
                            <FormFeedback
                              clientError={
                                methods.formState.errors.certifications?.[index]?.file__file
                              }
                              serverErrors={serverErrors?.body.certifications?.[index]?.file__file}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </li>
              ))}
            </ul>
            <Button
              className="d-flex align-items-center"
              variant="outline-primary"
              type="button"
              onClick={() => addCertification(emptyCertification)}
            >
              <BsPlus className="me-1" /> Add Life Support Certification
            </Button>
          </Form.Group>
          <Form.Group className="mb-3">
            <FormLabel visibility={FormFieldVisibility.CONNECTED} classNames="text-field-label">
              CSR License (if applicable)
            </FormLabel>
            <LicensesUploader
              controlId={'licenses'}
              licenseType={LicenseTypeEnum.CSR}
              control={methods.control}
              register={methods.register}
              errors={methods.formState.errors}
              className="provider-file-input"
              serverErrors={serverErrors}
              showServerFieldErrors={showServerFieldErrors}
              setShowServerFieldErrors={setShowServerFieldErrors}
              itemCaption="CSR License"
            />
          </Form.Group>
          <Row>
            <Col md={12}>
              <Button
                className="float-end"
                variant="primary"
                type="submit"
                onClick={() => handleSubmitClick()}
                disabled={
                  mutationProviderCurrentCredentialingLicensesUpdate.isLoading || !isSubmitEnabled
                }
              >
                Save Changes
              </Button>
            </Col>
          </Row>
          <ServerErrorsAlert
            errors={serverErrors}
            showErrors={showGlobalServerErrors}
            clearErrors={handleClearErrorsAlert}
          />
        </Form>
      </FormProvider>
      <UnsavedChangesFormPrompt
        hasUnsavedChanges={methods.formState.isDirty && !methods.formState.isSubmitSuccessful}
      />
    </Card>
  );
};

export const ProviderLicenses = () => {
  return (
    <ProviderFormContainer>
      <ProviderLicensesForm />
    </ProviderFormContainer>
  );
};
